import React from 'react';
import { Modal, Form, Button, Row, Col, Card, Badge, Alert, Spinner, Table } from 'react-bootstrap';
import { FaFileAlt, FaFilePdf, FaFileImage, FaUpload, FaTrash, FaEye, FaDownload } from 'react-icons/fa'; 
import useEmployeeDocuments from '../../hooks/useEmployeeDocuments';
import { validateTCNumber } from '../../utils/validators';

const EmployeeEditModal = ({
    showEditModal,
    setShowEditModal,
    formData,
    handleFormChange,
    handleUpdateEmployee,
    loading
}) => {
    // Document management 
    const {
        documents,
        uploading,
        error: docError,
        success: docSuccess,
        categories,
        loadDocuments,
        uploadDocument,
        deleteDocument,
        downloadDocument,
        getDocumentStats,
        clearMessages
    } = useEmployeeDocuments(formData?.employeeId || formData?._id);

    // Upload state 
    const [selectedFiles, setSelectedFiles] = React.useState([]);
    const [uploadCategory, setUploadCategory] = React.useState('nufus_cuzdani');
    const [uploadNotes, setUploadNotes] = React.useState('');

    // Load documents when modal opens
    React.useEffect(() => {
        const employeeId = formData?.employeeId || formData?._id;
        if (showEditModal && employeeId) {
            loadDocuments();
        }
    }, [showEditModal, formData?.employeeId, formData?._id, loadDocuments]);

    // Helper functions -  
    const getFileIcon = (mimeType) => {
        if (mimeType === 'application/pdf') return FaFilePdf;
        if (mimeType?.startsWith('image/')) return FaFileImage;
        return FaFileAlt;
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const handleFileSelect = (e) => {
        setSelectedFiles(Array.from(e.target.files));
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) return;

        for (const file of selectedFiles) {
            await uploadDocument(file, uploadCategory, uploadNotes);
        }
        setSelectedFiles([]);
        setUploadNotes('');
        // Clear file input
        const fileInput = document.getElementById('document-upload-edit');
        if (fileInput) fileInput.value = '';
    };

    const handleDeleteDocument = async (document) => {
        await deleteDocument(document._id, document.originalName);
    };

    const handleViewDocument = (document) => {
        window.open(document.url, '_blank');
    };

    const stats = getDocumentStats();

    return (
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="xl">
            <Modal.Header closeButton>
                <Modal.Title>✏️ Personel Düzenle</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleUpdateEmployee}>
                <Modal.Body>
                    <Row>
                        <Col md={6}>
                            <h6>Kişisel Bilgiler</h6>
                            <Form.Group className="mb-3">
                                <Form.Label>Ad Soyad *</Form.Label>
                                <Form.Control
                                    type="text"
                                    required
                                    value={formData.personalInfo.fullName}
                                    onChange={(e) => handleFormChange('personalInfo', 'fullName', e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>TC Kimlik No *</Form.Label>
                                <Form.Control
                                    type="text"
                                    required
                                    maxLength="11"
                                    value={formData.personalInfo.tcNo}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\D/g, ''); // Sadece rakam
                                        handleFormChange('personalInfo', 'tcNo', value);
                                    }}
                                    isInvalid={formData.personalInfo.tcNo && !validateTCNumber(formData.personalInfo.tcNo)}
                                />
                                <Form.Control.Feedback type="invalid">
                                    Geçerli bir TC Kimlik numarası giriniz
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Telefon *</Form.Label>
                                <Form.Control
                                    type="tel"
                                    required
                                    value={formData.personalInfo.phone}
                                    onChange={(e) => handleFormChange('personalInfo', 'phone', e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Email *</Form.Label>
                                <Form.Control
                                    type="email"
                                    required
                                    value={formData.personalInfo.email}
                                    onChange={(e) => handleFormChange('personalInfo', 'email', e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Doğum Tarihi *</Form.Label>
                                <Form.Control
                                    type="date"
                                    required
                                    value={formData.personalInfo.birthDate ? formData.personalInfo.birthDate.split('T')[0] : ''}
                                    onChange={(e) => handleFormChange('personalInfo', 'birthDate', e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Adres *</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    required
                                    value={formData.personalInfo.address}
                                    onChange={(e) => handleFormChange('personalInfo', 'address', e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>IBAN Sahibi İsmi</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.personalInfo.ibanHolder}
                                    onChange={(e) => handleFormChange('personalInfo', 'ibanHolder', e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>IBAN Numarası</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.personalInfo.ibanNumber}
                                    onChange={(e) => handleFormChange('personalInfo', 'ibanNumber', e.target.value)}
                                    placeholder="TR00 0000 0000 0000 0000 0000 00"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <h6>İş Bilgileri</h6>
                            <Form.Group className="mb-3">
                                <Form.Label>Pozisyon *</Form.Label>
                                <Form.Control
                                    type="text"
                                    required
                                    value={formData.workInfo.position}
                                    onChange={(e) => handleFormChange('workInfo', 'position', e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Departman *</Form.Label>
                                <Form.Select
                                    required
                                    value={formData.workInfo.department}
                                    onChange={(e) => handleFormChange('workInfo', 'department', e.target.value)}
                                >
                                    <option value="satış">Satış</option>
                                    <option value="muhasebe">Muhasebe</option>
                                    <option value="insan_kaynakları">İnsan Kaynakları</option>
                                    <option value="bilgi_işlem">Bilgi İşlem</option>
                                    <option value="genel_koordinatör">Genel Koordinatör</option>
                                    <option value="temizlik">Temizlik</option>
                                    <option value="güvenlik">Güvenlik</option>
                                    <option value="diğer">Diğer</option>
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>İşe Başlama Tarihi *</Form.Label>
                                <Form.Control
                                    type="date"
                                    required
                                    value={formData.workInfo.startDate ? formData.workInfo.startDate.split('T')[0] : ''}
                                    onChange={(e) => handleFormChange('workInfo', 'startDate', e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Sözleşme Türü *</Form.Label>
                                <Form.Select
                                    required
                                    value={formData.workInfo.contractType}
                                    onChange={(e) => handleFormChange('workInfo', 'contractType', e.target.value)}
                                >
                                    <option value="belirsiz_sureli">Belirsiz Süreli</option>
                                    <option value="belirli_sureli">Belirli Süreli</option>
                                    <option value="deneme_sureli">Deneme Süreli</option>
                                </Form.Select>
                            </Form.Group>
                            {formData.workInfo.contractType !== 'belirsiz_sureli' && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Sözleşme Bitiş Tarihi *</Form.Label>
                                    <Form.Control
                                        type="date"
                                        required
                                        value={formData.workInfo.contractEndDate}
                                        onChange={(e) => handleFormChange('workInfo', 'contractEndDate', e.target.value)}
                                    />
                                </Form.Group>
                            )}
                            <Form.Group className="mb-3">
                                <Form.Label>Maaş *</Form.Label>
                                <Form.Control
                                    type="number"
                                    required
                                    min="0"
                                    step="0.01"
                                    value={formData.workInfo.salary}
                                    onChange={(e) => handleFormChange('workInfo', 'salary', e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>İş Durumu *</Form.Label>
                                <Form.Select
                                    required
                                    value={formData.workInfo.workStatus}
                                    onChange={(e) => handleFormChange('workInfo', 'workStatus', e.target.value)}
                                >
                                    <option value="aktif">Aktif</option>
                                    <option value="izinli">İzinli</option>
                                    <option value="ayrılmış">Ayrılmış</option>
                                    <option value="sözleşme_bitmiş">Sözleşme Bitmiş</option>
                                </Form.Select>
                            </Form.Group>

                            <h6 className="mt-4">Acil Durum İletişim</h6>
                            <Form.Group className="mb-3">
                                <Form.Label>Ad Soyad *</Form.Label>
                                <Form.Control
                                    type="text"
                                    required
                                    value={formData.personalInfo.emergencyContact.name}
                                    onChange={(e) => handleFormChange('emergencyContact', 'name', e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Telefon *</Form.Label>
                                <Form.Control
                                    type="tel"
                                    required
                                    value={formData.personalInfo.emergencyContact.phone}
                                    onChange={(e) => handleFormChange('emergencyContact', 'phone', e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Yakınlık Derecesi *</Form.Label>
                                <Form.Select
                                    required
                                    value={formData.personalInfo.emergencyContact.relation}
                                    onChange={(e) => handleFormChange('emergencyContact', 'relation', e.target.value)}
                                >
                                    <option value="eş">Eş</option>
                                    <option value="anne">Anne</option>
                                    <option value="baba">Baba</option>
                                    <option value="kardeş">Kardeş</option>
                                    <option value="çocuk">Çocuk</option>
                                    <option value="arkadaş">Arkadaş</option>
                                    <option value="diğer">Diğer</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* BELGELER YÖNETİMİ -   */}
                    <hr className="my-4" />

                    {/* Document Error/Success Messages */}
                    {docError && (
                        <Alert variant="danger" dismissible onClose={clearMessages}>
                            {docError}
                        </Alert>
                    )}
                    {docSuccess && (
                        <Alert variant="success" dismissible onClose={clearMessages}>
                            {docSuccess}
                        </Alert>
                    )}

                    {/* Document Stats */}
                    <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">📎 Belge Yönetimi</h6>
                            <div className="d-flex gap-2">
                                <Badge bg="primary">{stats.total} Toplam</Badge>
                                <Badge bg="success">{stats.requiredComplete}/{stats.requiredTotal} Zorunlu</Badge>
                                <Badge bg="info">{stats.completionPercentage}% Tamamlama</Badge>
                            </div>
                        </div>

                        {/* Upload Section */}
                        <Card className="mb-3">
                            <Card.Body className="p-3">
                                <Row className="align-items-end">
                                    <Col md={3}>
                                        <Form.Group className="mb-2">
                                            <Form.Label className="small">Kategori</Form.Label>
                                            <Form.Select
                                                size="sm"
                                                value={uploadCategory}
                                                onChange={(e) => setUploadCategory(e.target.value)}
                                            >
                                                {categories.map(cat => (
                                                    <option key={cat.id} value={cat.id}>
                                                        {cat.label} {cat.required ? '*' : ''}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-2">
                                            <Form.Label className="small">Dosya Seç</Form.Label>
                                            <Form.Control
                                                id="document-upload-edit"
                                                type="file"
                                                size="sm"
                                                multiple
                                                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                                                onChange={handleFileSelect}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-2">
                                            <Form.Label className="small">Notlar</Form.Label>
                                            <Form.Control
                                                type="text"
                                                size="sm"
                                                placeholder="Opsiyonel"
                                                value={uploadNotes}
                                                onChange={(e) => setUploadNotes(e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Button
                                            size="sm"
                                            variant="success"
                                            onClick={handleUpload}
                                            disabled={selectedFiles.length === 0 || uploading}
                                            className="w-100"
                                        >
                                            {uploading ? (
                                                <Spinner animation="border" size="sm" />
                                            ) : (
                                                <>
                                                    <FaUpload className="me-1" />
                                                    Yükle
                                                </>
                                            )}
                                        </Button>
                                    </Col>
                                </Row>

                                {selectedFiles.length > 0 && (
                                    <div className="mt-2">
                                        <small className="text-muted">
                                            Seçili: {selectedFiles.map(f => f.name).join(', ')}
                                        </small>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>

                        {/* Documents List */}
                        {documents.length > 0 ? (
                            <div className="table-responsive">
                                <Table striped hover className="mb-0">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>Kategori</th>
                                            <th>Dosya Adı</th>
                                            <th>Yükleme Tarihi</th>
                                            <th>Yükleyen</th>
                                            <th width="160">İşlemler</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {documents.map((document) => {
                                            const category = categories.find(c => c.id === document.category);

                                            return (
                                                <tr key={document._id}>
                                                    <td>
                                                        <Badge bg={category?.color || 'secondary'} className="px-2 py-1">
                                                            {category?.label || document.category}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-truncate" style={{ maxWidth: '200px' }}>
                                                        <span title={document.originalName}>
                                                            {document.originalName}
                                                        </span>
                                                        <br />
                                                        <small className="text-muted">{formatFileSize(document.fileSize)}</small>
                                                    </td>
                                                    <td>
                                                        <small>{new Date(document.uploadDate).toLocaleDateString('tr-TR')}</small>
                                                    </td>
                                                    <td>
                                                        <small className="text-muted">
                                                            {document.uploadedBy?.name || 'Bilinmiyor'}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-1">
                                                            <Button
                                                                size="sm"
                                                                variant="outline-primary"
                                                                onClick={() => handleViewDocument(document)}
                                                                title="Görüntüle"
                                                            >
                                                                <FaEye size={12} />
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline-success"
                                                                onClick={() => downloadDocument(document)}
                                                                title="İndir"
                                                            >
                                                                <FaDownload size={12} />
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline-danger"
                                                                onClick={() => handleDeleteDocument(document)}
                                                                title="Sil"
                                                            >
                                                                <FaTrash size={12} />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        ) : (
                            <div className="text-center py-4 text-muted">
                                <FaFileAlt className="mb-2" size={32} />
                                <p className="mb-0">Henüz belge yüklenmemiş</p>
                                <small>Yukarıdaki formu kullanarak belge ekleyebilirsiniz</small>
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                        İptal
                    </Button>
                    <Button type="submit" variant="primary" disabled={loading}>
                        {loading ? 'Güncelleniyor...' : 'Güncelle'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default EmployeeEditModal;